// frontend/src/pages/dashboard/fbs/index.js
import { useDispatch, useSelector } from "react-redux";
import { setMarket, setStatus } from "../../../redux/slices/uiSlice";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import PostingsTable from "../../../components/table/PostingsTable";
import usePostings from "../../../hooks/usePostings";
import usePostingItems from "../../../hooks/usePostingItems";
import useWithdrawalList from "../../../hooks/useWithdrawal";
import useNeedReturnCount from "../../../hooks/useNeedReturnCount";
import { useSearchParams } from "react-router-dom";
import {
  computeKizStatus,
  computeReturnStatus,
  computeWithdrawalStatus,
  saveAdditionalKizCodes,
  saveKizForItem,
  sendKizToWb,
  setItemReturnStatus,
  setItemWithdrawalStatus,
  updatePostingKizStatus,
  updatePostingReturnStatus,
  updatePostingWithdrawalStatus,
} from "../../../api/kiz";
import PostingItemsCards from "../../../components/posting/PostingItemsCards";

import OzonSyncButton from "../../../components/layout/OzonSyncButton";
import YmSyncButton from "../../../components/layout/YmSyncButton";
import WbSyncButton from "../../../components/layout/WbSyncButton";
import ReloadButton from "../../../components/layout/ReloadButton";
import { useSnackbar } from "notistack";
import DownloadExcelButton from "../../../components/layout/DownloadExcelButton";
import { fetchAllPostings } from "../../../api/postings";
import { fetchAllWithdrawal } from "../../../api/withdrawal";
import { exportToExcel } from "../../../utils/exportToExcel";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import { COLORS } from "../../../utils/colors";
import { getKizValidationError, getShortKiz, normalizeKizInput } from "../../../utils/kiz";

const statuses = [
  { key: "awaiting_packaging", label: "На сборке" },
  { key: "withdrawal", label: "Нужно вывести КИЗ" },
  { key: "ready_for_shipment", label: "Готов к отгрузке" },
  { key: "delivering", label: "В доставке" },
  { key: "delivered", label: "Доставлено" },
  { key: "cancelled", label: "Отменено" },
];

const extractKizRowsFromItem = (item) => {
  const rows = [];
  const defaultPrice = item?.price === undefined || item?.price === null
    ? ""
    : String(item.price).trim();

  const baseCode = String(item?.kiz_code || "").trim();
  if (baseCode) {
    rows.push({ code: baseCode, fullCode: baseCode, price: defaultPrice });
  }

  const additionalCodes = Array.isArray(item?.kiz_additional_codes)
    ? item.kiz_additional_codes
    : [];

  additionalCodes.forEach((entry) => {
    if (typeof entry === "string") {
      const code = entry.trim();
      if (code) rows.push({ code, fullCode: code, price: defaultPrice });
      return;
    }

    if (entry && typeof entry === "object") {
      const code = String(entry.kiz_code || entry.code || "").trim();
      if (!code) return;

      const hasPrice = entry.price !== undefined && entry.price !== null && String(entry.price).trim() !== "";
      rows.push({
        code,
        fullCode: code,
        price: hasPrice ? String(entry.price).trim() : defaultPrice,
      });
    }
  });

  return rows;
};

const buildExcelRowsByKiz = (posting) => {
  const baseRow = {
    Заказ: posting.posting_number,
    Маркет: (posting.market || "").toUpperCase(),
    "WB Номер заказа": posting.wbOrderId || "",
    Товар: posting.product_title || posting.article || "—",
    "Дата заказа": posting.in_process_at
      ? new Date(posting.in_process_at).toLocaleString()
      : "",
    "Дата доставки": posting.shipment_date
      ? new Date(posting.shipment_date).toLocaleString()
      : "",
    "Статус КИЗ": posting.kiz_status === "complete" ? "Добавлен" : "Не добавлен",
    "КИЗ в WB": posting.kiz_sent ? "Отправлен" : "Не отправлен",
    "Статус возврата":
      posting.return_status === "returned_all"
        ? "Возвращён"
        : posting.return_status === "need_return"
          ? "Надо вернуть"
          : "—",
    "Статус продажи":
      posting.withdrawal_status === "sold" ? "Продан" : "На балансе",
  };

  const items = Array.isArray(posting.items) ? posting.items : [];
  const kizRows = items.flatMap(extractKizRowsFromItem);

  if (kizRows.length === 0) {
    return [{ "Коды КИЗ": "", Цена: "", ...baseRow, "Полный КИЗ": "" }];
  }

  return kizRows.map((kizRow) => ({
    "Коды КИЗ": getShortKiz(kizRow.code),
    Цена: kizRow.price,
    ...baseRow,
    "Полный КИЗ": kizRow.fullCode,
  }));
};

export default function FbsPage() {
  const dispatch = useDispatch();
  const market = useSelector((s) => s.ui.market);
  const status = useSelector((s) => s.ui.status);
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const allowedMarkets = ["ozon", "wb", "ym"];
  const allowedStatuses = statuses.map((s) => s.key);

  // серверная пагинация (0-based в UI)
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedRow, setSelectedRow] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [sendingWbId, setSendingWbId] = useState(null);
  const [savingAdditionalId, setSavingAdditionalId] = useState(null);

  const isWithdrawal = status === "withdrawal";

  // 0-based страница в usePostings, а для «вывода» у вас была 1-based
  const p = usePostings({
    market,
    state: status,
    page,
    pageSize,
    reloadToken,
    enabled: !isWithdrawal, // 👈 этот хук активен, если НЕ вкладка вывода
  });

  const w = useWithdrawalList({
    market,
    page: page + 1, // 👈 если у вас 1-based в этом хуке
    pageSize,
    enabled: isWithdrawal, // 👈 активен, когда вкладка «Нужно вывести КИЗ»
    reloadToken,
  });

  // счётчик позиций с нужным КИЗ
  const { total: withdrawalCount } = useWithdrawalList({
    market,
    page: 1,
    pageSize: 1,
    enabled: true,
    reloadToken,
  });
  const cancelledCount = useNeedReturnCount({ market });
  const rows = isWithdrawal ? w.rows : p.rows;
  const total = isWithdrawal ? w.total : p.total;
  const loading = isWithdrawal ? w.loading : p.loading;
  // 2) Позиции выбранного постинга (для правой карточки)
  const { items, loading: itemsLoading } = usePostingItems(selectedRow?.id);

  // локальная копия позиций
  const [itemsLocal, setItemsLocal] = useState([]);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => setItemsLocal(items), [items]);

  // Читаем выбранные маркет и статус из URL, чтобы при перезагрузке
  // оставаться на той же таблице.
  useEffect(() => {
    const urlMarket = searchParams.get("market");
    if (
      urlMarket &&
      allowedMarkets.includes(urlMarket) &&
      urlMarket !== market
    ) {
      dispatch(setMarket(urlMarket));
    }

    const urlStatus = searchParams.get("status");
    if (
      urlStatus &&
      allowedStatuses.includes(urlStatus) &&
      urlStatus !== status
    ) {
      dispatch(setStatus(urlStatus));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Синхронизируем URL с текущим выбором, чтобы сохранить его при перезагрузке.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        const currentMarket = params.get("market");
        const currentStatus = params.get("status");

        if (currentMarket === market && currentStatus === status) {
          return prev;
        }

        params.set("market", market);
        params.set("status", status);
        return params;
      },
      { replace: true },
    );
  }, [market, status, setSearchParams]);

  // Сохранение КИЗ по Enter
  async function saveKizInline(row, code) {
    const clean = normalizeKizInput(code);
    if (!clean) return;
    const validationError = getKizValidationError(clean);
    if (validationError) {
      enqueueSnackbar(validationError, { variant: "error" });
      return;
    }
    try {
      setSavingId(row.documentId);
      await saveKizForItem(row.documentId, clean);

      const next = itemsLocal.map((it) =>
        it.documentId === row.documentId ? { ...it, kiz_code: clean } : it,
      );
      setItemsLocal(next);

      const newStatus = computeKizStatus(next);
      await updatePostingKizStatus(selectedRow.documentId, newStatus);
      enqueueSnackbar(`КИЗ прикреплен к ${row.name}`, { variant: "success" });
      setReloadToken((x) => x + 1);
    } catch (e) {
      console.error(e);
      enqueueSnackbar(`Не удалось прикреплен КИЗ к ${row.name}`, {
        variant: "error",
      });
    } finally {
      setSavingId(null);
    }
  }

  async function addAdditionalKizInline(row, code) {
    const clean = normalizeKizInput(code);
    if (!clean) return;
    const validationError = getKizValidationError(clean);
    if (validationError) {
      enqueueSnackbar(validationError, { variant: "error" });
      return;
    }
    const current = Array.isArray(row.kiz_additional_codes)
      ? row.kiz_additional_codes
      : [];
    if (current.includes(clean)) return;

    try {
      setSavingAdditionalId(row.documentId);
      const nextCodes = [...current, clean];
      await saveAdditionalKizCodes(row.documentId, nextCodes);

      const next = itemsLocal.map((it) =>
        it.documentId === row.documentId
          ? { ...it, kiz_additional_codes: nextCodes }
          : it,
      );
      setItemsLocal(next);
      enqueueSnackbar(`Доп. КИЗ добавлен к ${row.name}`, {
        variant: "success",
      });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(`Не удалось сохранить доп. КИЗ для ${row.name}`, {
        variant: "error",
      });
    } finally {
      setSavingAdditionalId(null);
    }
  }

  async function removeAdditionalKizInline(row, code) {
    const clean = String(code || "").trim();
    if (!clean) return;
    const current = Array.isArray(row.kiz_additional_codes)
      ? row.kiz_additional_codes
      : [];

    const nextCodes = current.filter((c) => c !== clean);
    if (nextCodes.length === current.length) return;

    try {
      setSavingAdditionalId(row.documentId);
      await saveAdditionalKizCodes(row.documentId, nextCodes);

      const next = itemsLocal.map((it) =>
        it.documentId === row.documentId
          ? { ...it, kiz_additional_codes: nextCodes }
          : it,
      );
      setItemsLocal(next);
      enqueueSnackbar(`Доп. КИЗ удален у ${row.name}`, { variant: "success" });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(`Не удалось удалить доп. КИЗ у ${row.name}`, {
        variant: "error",
      });
    } finally {
      setSavingAdditionalId(null);
    }
  }

  // Смена «КИЗ возвращён в оборот» (для статуса Отменено)
  async function setReturnInline(row, returned) {
    try {
      await setItemReturnStatus(row.documentId, returned);

      const next = itemsLocal.map((it) =>
        it.documentId === row.documentId
          ? { ...it, kiz_returned: !!returned }
          : it,
      );
      setItemsLocal(next);

      const agg = computeReturnStatus(next);
      await updatePostingReturnStatus(selectedRow.documentId, agg);
      enqueueSnackbar(`КИЗ заказа ${row.name} возвращен в обоорот `, {
        variant: "success",
      });

      if (returned) {
        const cleanCode = String(row.kiz_code || "").trim();
        const prefix = "Вернул в продажу | ";
        if (cleanCode) {
          const updatedCode = cleanCode.startsWith(prefix)
            ? cleanCode
            : `${prefix}${cleanCode}`;

          try {
            await saveKizForItem(row.documentId, updatedCode);
            setItemsLocal((prev) =>
              prev.map((it) =>
                it.documentId === row.documentId
                  ? { ...it, kiz_code: updatedCode }
                  : it,
              ),
            );
          } catch (error) {
            console.error("[return] save kiz code fail", error);
            enqueueSnackbar(
              "КИЗ возвращен, но не удалось обновить код в позиции",
              {
                variant: "warning",
              },
            );
          }
        }
      }
      setReloadToken((x) => x + 1);
    } catch (e) {
      console.error("[return] fail", e);
      enqueueSnackbar(`Не удалось вернуть в оборот КИЗ к ${row.name}`, {
        variant: "error",
      });
    }
  }

  async function setSoldInline(row, nextStatus /* 'on_balance' | 'sold' */) {
    try {
      // 1) сохраняем в позиции
      await setItemWithdrawalStatus(row.documentId, nextStatus);

      // 2) обновляем локальное состояние
      const nextItems = itemsLocal.map((it) =>
        it.documentId === row.documentId
          ? { ...it, withdrawal_status: nextStatus }
          : it,
      );
      setItemsLocal(nextItems);

      // 3) считаем агрегат и пишем в posting
      const agg = computeWithdrawalStatus(nextItems);
      await updatePostingWithdrawalStatus(selectedRow.documentId, agg);
      enqueueSnackbar(`КИЗ заказа ${row.name} - продан`, {
        variant: "success",
      });
      // 4) дергаем перезагрузку левой таблицы
      setReloadToken((x) => x + 1);
    } catch (e) {
      console.error("[withdrawal] fail", e);
      enqueueSnackbar(`Не удалось утсановить продажу КИЗ ${row.name}`, {
        variant: "error",
      });
    }
  }

  async function sendKizToWbInline(row) {
    const code = String(row.kiz_code || "").trim();
    if (!code) return;
    try {
      setSendingWbId(row.documentId);
      await sendKizToWb(row.documentId);

      const next = itemsLocal.map((it) =>
        it.documentId === row.documentId ? { ...it, kiz_sent: true } : it,
      );
      setItemsLocal(next);

      setSelectedRow((prev) => (prev ? { ...prev, kiz_sent: true } : prev));

      enqueueSnackbar(`КИЗ ${row.name} отправлен в WB`, {
        variant: "success",
      });
      setReloadToken((x) => x + 1);
    } catch (e) {
      console.error("[wb send] fail", e);
      const reason = e?.response?.data?.error?.message || e?.message;
      enqueueSnackbar(`Не удалось отправить КИЗ для ${row.name}${reason ? `: ${reason}` : ""}`, {
        variant: "error",
      });
    } finally {
      setSendingWbId(null);
    }
  }

  async function exportPostings() {
    const allRows = isWithdrawal
      ? await fetchAllWithdrawal({ market })
      : await fetchAllPostings({ market, state: status });
    const rowsForExcel = allRows.flatMap(buildExcelRowsByKiz);

    exportToExcel({
      rows: rowsForExcel,
      sheetName: "Postings",
      fileName: `postings_${market}_${status}`,
    });
  }

  const pillSx = useMemo(
    () => ({
      textTransform: "none",
      minHeight: 36,
      height: 36,
      px: 1.75,
      mx: 0.5,
      borderRadius: 1,
      fontWeight: 600,
      "&.Mui-selected": {
        bgcolor:
          market === "ym"
            ? COLORS.YM.bgcolor
            : market === "wb"
              ? COLORS.WB.bgcolor
              : market === "ozon"
                ? COLORS.OZON.bgcolor
                : COLORS.DEFAULT.bgcolor,
        color:
          market === "ym"
            ? COLORS.YM.color
            : market === "wb"
              ? COLORS.WB.color
              : market === "ozon"
                ? COLORS.OZON.color
                : COLORS.DEFAULT.color,
      },
    }),
    [market],
  );

  const indicatorColor = useMemo(() => {
    if (market === "ym") return COLORS.YM.dark;
    if (market === "wb") return COLORS.WB.dark;
    if (market === "ozon") return COLORS.OZON.dark;
    return COLORS.DEFAULT.dark;
  }, [market]);

  return (
    <DashboardLayout
      market={market}
      onSelectMarket={(v) => {
        dispatch(setMarket(v));
        setPage(0);
        setSelectedRow(null);
      }}
    >
      <Stack spacing={2}>
        <Paper
          sx={{
            p: { xs: 1.5, md: 2 },
          }}
          variant="outlined"
        >
          <Tabs
            value={status}
            variant="outlined"
            allowScrollButtonsMobile
            onChange={(_, v) => {
              dispatch(setStatus(v));
              setPage(0);
              setSelectedRow(null);
            }}
            TabIndicatorProps={{
              style: { backgroundColor: indicatorColor, height: 2 },
            }}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": pillSx,
              "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
            }}
          >
            <Tab value="awaiting_packaging" label="На сборке" />
            <Tab value="ready_for_shipment" label="Готов к отгрузке" />
            <Tab value="delivering" label="В доставке" />
            <Tab value="delivered" label="Доставлено" />
            <Tab
              value="withdrawal"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>Нужно вывести КИЗ</span>
                  {withdrawalCount > 0 && (
                    <Chip label={withdrawalCount} size="small" color="error" />
                  )}
                </Box>
              }
            />
            <Tab
              value="cancelled"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>Отменено</span>
                  {cancelledCount > 0 && (
                    <Chip label={cancelledCount} size="small" color="warning" />
                  )}
                </Box>
              }
            />
          </Tabs>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 380px" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Paper
            sx={{
              p: 2,
              minWidth: 0,
              overflow: "hidden",
            }}
            variant="outlined"
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1, gap: 1 }}
            >
              <Typography variant="subtitle1">
                {market.toUpperCase()} ·{" "}
                {statuses.find((s) => s.key === status)?.label}
              </Typography>

              <Stack direction="row" spacing={1}>
                <DownloadExcelButton
                  onExport={exportPostings}
                  disabled={loading}
                />
                <ReloadButton
                  setReloadToken={setReloadToken}
                  loading={loading}
                />
                {market === "ozon" && (
                  <OzonSyncButton setReloadToken={setReloadToken} />
                )}
                {market === "ym" && (
                  <YmSyncButton setReloadToken={setReloadToken} />
                )}
                {market === "wb" && (
                  <WbSyncButton setReloadToken={setReloadToken} />
                )}
              </Stack>
            </Stack>

            <PostingsTable
              rows={rows}
              total={total}
              loading={loading}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(0);
              }}
              onSelect={(row) => setSelectedRow(row)}
              status={status}
              market={market}
              selectedRowId={selectedRow?.id}
            />
          </Paper>

          <Paper
            sx={{
              p: 2,
              position: { lg: "sticky" },
              top: { lg: 16 },
              height: { lg: "calc(100vh - 32px - 64px)" },
              maxHeight: { lg: "calc(100vh - 32px - 64px)" },
              overflowY: { lg: "auto" },
              overflowX: "hidden",
            }}
          >
            {!selectedRow ? (
              <div style={{ color: "#6b7280" }}>Выберите заказ слева</div>
            ) : itemsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <PostingItemsCards
                posting={selectedRow}
                items={itemsLocal}
                orderStatus={status}
                savingId={savingId}
                onChangeKiz={(it, v) => {
                  setItemsLocal((prev) =>
                    prev.map((row) =>
                      row.id === it.id ? { ...row, kiz_code: v } : row,
                    ),
                  );
                }}
                onSaveKiz={(it) => saveKizInline(it, it.kiz_code)}
                onAddAdditionalKiz={(it, code) =>
                  addAdditionalKizInline(it, code)
                }
                onRemoveAdditionalKiz={(it, code) =>
                  removeAdditionalKizInline(it, code)
                }
                savingAdditionalId={savingAdditionalId}
                onSetReturn={(it, returned) => setReturnInline(it, returned)}
                onSetSold={(it, next) => setSoldInline(it, next)} // <— ВАЖНО
                onSendToWb={(it) => sendKizToWbInline(it)}
                sendingWbId={sendingWbId}
              />
            )}
          </Paper>
        </Box>
      </Stack>
    </DashboardLayout>
  );
}
